import React, { useState } from 'react'

import { StructuredText } from '@mieterengel/database-types'
import classNames from 'classnames'
import { Align, Avatar, Spacer } from 'component-library-next'
import { themeColors } from 'component-library-next/build/colors'
import arrowHoveredSrc from 'component-library-next/build/images/accordion-hover.svg'
import arrowIneffectiveSrc from 'component-library-next/build/images/accordion-ineffective.svg'
import arrowSrc from 'component-library-next/build/images/accordion.svg'
import robbie from 'component-library-next/build/images/robbie.svg'
import cloneDeep from 'lodash.clonedeep'
import { Transforms } from 'slate'

import { useTemplatedForm } from '@app/lib/context/TemplatedForm'
import { RenovationClause } from '@app/modules/Messages/Components/RenovationClauses/RenovationClause'
import { useTemplateCollectionQuery } from '@app/queries/TemplateCollection/TemplateCollection.queries'

import './RenovationClause.scss'

type ClauseSpan = {
  text?: string
  page?: number
}
type IdentifiedClause = {
  type: string
  effective: boolean
  spans: ClauseSpan[]
}

type Document = {
  id: number
  name: string
  fileUrl: string
  previewImageUrl?: string
  fileFormat?: string
  documentDate: Date
  effective?: boolean
  identifiedClauses?: IdentifiedClause[]
}

type OwnProps = {
  doc: Document
  clauses: IdentifiedClause[]
}

export const RenovationClauses: any = ({ doc, clauses }: OwnProps) => {
  const [showClauses, setShowClauses] = useState(true)
  const [arrowHovered, setArrowHovered] = useState(false)

  const ctx = useTemplatedForm()
  const findingsEditor = ctx?.editors.get('findings')
  const recommendationsEditor = ctx?.editors.get('recommendations')

  const { data } = useTemplateCollectionQuery({
    variables: {
      filters: {
        uid: {
          equals: 'Lawyer.AuditReport.Topic.Renovation'
        }
      }
    }
  })

  const templates = data?.templateCollection.templates || undefined

  const addTemplate = (findingsText: StructuredText, recommendationsText: StructuredText) => {
    if (findingsEditor) {
      Transforms.insertNodes(findingsEditor, cloneDeep(findingsText.map((text) => text)))
    }

    if (recommendationsEditor) {
      Transforms.insertNodes(
        recommendationsEditor,
        cloneDeep(recommendationsText.map((text) => text))
      )
    }
  }

  const toggleClause = () => {
    setShowClauses(!showClauses)
  }

  return (
    <React.Fragment key={doc.id}>
      <Spacer />
      <div className='mecl-clauses__clause-box-wrapper'>
        <div className='mecl-clauses__avatar-wrapper'>
          <Avatar
            borderColor={doc.effective !== false ? themeColors.blackBorder : themeColors.warning}
            image={robbie}
            alt='rob robot icon'
          />
        </div>
        <div
          className={classNames(
            'mecl-clauses__clause-box mecl-clauses__clause-box--message',
            {
              'mecl-clauses__clause-box mecl-clauses__clause-box--message--clickable': !showClauses
            },
            {
              'mecl-clauses__clause-box mecl-clauses__clause-box--message--ineffective':
                doc.effective === false
            }
          )}
          onClick={() => !showClauses && toggleClause()}
        >
          <Align direction={Align.directions.spaceBetween}>
            <p className='mecl-clauses__header'>
              <span className='mecl-clauses__clause-box mecl-clauses__clause-box--opening'>
                Schönheitsreparatur-Check:
              </span>{' '}
              »{doc.name}«
              <br />
              {doc.effective === false && (
                <span className='mecl-clauses__clause-box mecl-clauses__clause-box--ineffective-text'>
                  Vorläufige Einschätzung: Gesamtunwirksamkeit (!){' '}
                </span>
              )}
              <span
                className='mecl-clauses__clause-box mecl-clauses__clause-box--toggle-text'
                onClick={toggleClause}
              >
                Gefundene Klauseln {showClauses ? 'ausblenden' : 'einblenden'}
              </span>
            </p>
            <img
              src={
                doc.effective === false
                  ? arrowIneffectiveSrc
                  : arrowHovered
                  ? arrowHoveredSrc
                  : arrowSrc
              }
              onClick={toggleClause}
              className={classNames('mecl-clauses__arrow', {
                'mecl-clauses__arrow--up': showClauses
              })}
              onMouseOver={() => doc.effective !== false && setArrowHovered(true)}
              onMouseLeave={() => doc.effective !== false && setArrowHovered(false)}
              alt='Robby &Ouml;ffnen/Schließen'
            />
          </Align>
          {showClauses &&
            `<Spacer/>` &&
            clauses.map((clause) => {
              return (
                <RenovationClause
                  clause={clause}
                  addTemplate={addTemplate}
                  documentDate={new Date(doc.documentDate)}
                  templates={templates}
                  hasEditor={!!findingsEditor || !!recommendationsEditor}
                />
              )
            })}
        </div>
      </div>
    </React.Fragment>
  )
}
